import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { getReceiptHtml } from "../../apis/customerProfile/apis";
import { strings } from "../../I18n";

interface Props {
  open: boolean;
  paymentKey: string | null;
  onClose: () => void;
}

const ReceiptDrawer: React.FC<Props> = ({ open, paymentKey, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receiptHtml, setReceiptHtml] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (open && paymentKey) {
      const fetchReceipt = async () => {
        setLoading(true);
        setError(null);
        try {
          const html = await getReceiptHtml(paymentKey);
          setReceiptHtml(html);
        } catch (e) {
          setError("Failed to load receipt");
        } finally {
          setLoading(false);
        }
      };
      fetchReceipt();
    } else {
      setReceiptHtml(null);
      setError(null);
    }
  }, [open, paymentKey]);

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownloadPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 600,
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {strings("descriptions.receipt")}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : receiptHtml ? (
          <>
            <Box
              sx={{
                flex: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <iframe
                ref={iframeRef}
                srcDoc={receiptHtml}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Receipt"
              />
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                {strings("buttons.print")}
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPdf}
              >
                {strings("buttons.downloadPdf")}
              </Button>
            </Stack>
          </>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default ReceiptDrawer;
